import { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, X, Check } from 'lucide-react';
import { useClientContext, type LegalClient } from '../contexts/ClientContext';

export function ClientFilterDropdown() {
  const { selectedClient, clients, clientsLoading, selectClient, clearClient } = useClientContext();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const activeClients = clients.filter(c => c.status === 'active');

  function handleSelect(client: LegalClient) {
    selectClient(client);
    setOpen(false);
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    clearClient();
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
          selectedClient
            ? 'bg-primary/20 border border-primary/30 text-primary hover:bg-primary/30'
            : 'bg-secondary border border-border text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`}
      >
        <Building2 className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 truncate">
          {selectedClient ? selectedClient.name : 'All Clients'}
        </span>
        {selectedClient ? (
          <X
            className="w-3.5 h-3.5 flex-shrink-0 hover:text-foreground transition-colors"
            onClick={handleClear}
          />
        ) : (
          <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-xl overflow-hidden">
          {clientsLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : activeClients.length === 0 ? (
            <div className="px-3 py-4 text-xs text-muted-foreground text-center">No clients yet</div>
          ) : (
            <div className="max-h-60 overflow-y-auto py-1">
              {/* All option */}
              <button
                onClick={() => { clearClient(); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary transition-colors"
              >
                <div className="w-6 h-6 rounded bg-secondary flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <span className="flex-1 text-foreground">All Clients</span>
                {!selectedClient && <Check className="w-3.5 h-3.5 text-primary" />}
              </button>

              <div className="border-t border-border my-1" />

              {activeClients.map(client => (
                <button
                  key={client.id}
                  onClick={() => handleSelect(client)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary transition-colors"
                >
                  <div className="w-6 h-6 rounded bg-secondary flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {client.logo_url ? (
                      <img src={client.logo_url} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-xs font-bold text-muted-foreground">
                        {client.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-foreground truncate">{client.name}</div>
                    {client.domain && (
                      <div className="text-xs text-muted-foreground truncate">{client.domain}</div>
                    )}
                  </div>
                  {selectedClient?.id === client.id && (
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
